import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, shareReplay } from 'rxjs/operators';

import { KeyCode } from '../../domain/key-code.enum';
import { ModalService } from '../modal/modal.service';
import { LinkRegistryService } from '../../services/link-registry.service';
import { WINDOW_TOKEN } from '../../services/bootstrap.service';
import { AboutDialogComponent } from './about-dialog.component';
import { AboutData, AboutDialogConfiguration, ActuatorInfo } from './about-dialog.domain';

export type { AboutData, AboutDialogConfiguration, ActuatorInfo } from './about-dialog.domain';

import type { Observable } from 'rxjs';
import type { ModalRef } from '../modal/modal-ref';
import type { PostBootstrapInit } from '../../services/post-bootstrap-init';

export const ABOUT_DIALOG_CONFIGURATION = new InjectionToken<AboutDialogConfiguration>('aboutConfiguration');

interface ActuatorDataServer {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'Build-Tag': string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'Build-Number': string;
}

/**
 * About Dialog Service
 *
 * Collect browser and Webbroker configuration information for about dialog modal
 * to display. User agent, platform, and app version are from the browser window.
 * Release and version are from the configuration service.
 *
 */
@Injectable({
  providedIn: 'root'
})
export class AboutDialogService implements PostBootstrapInit {
  private readonly modalService = inject(ModalService);
  private readonly linkRegistry = inject(LinkRegistryService);
  private readonly httpClient = inject(HttpClient);
  private readonly browserWindow = inject(WINDOW_TOKEN);
  private readonly configuration: AboutDialogConfiguration | null = inject(ABOUT_DIALOG_CONFIGURATION, { optional: true });

  private openModal: ModalRef<AboutDialogComponent> | undefined;
  private readonly version: string = this.configuration?.version ?? 'N/A';
  private readonly revision: string = this.configuration?.revision ?? 'N/A';
  private readonly locationId: string | undefined = this.configuration?.locationId;
  private $actuatorData: Observable<ActuatorInfo>;

  /**
   * Listen for Key down event on an entire document. Once a keyboard combination
   * ctrl + alt + i is pressed, open the about dialog modal. If the modal is
   * already open, close the dialog modal.
   */
  init(): void {
    this.browserWindow.addEventListener('keydown', (event: KeyboardEvent): void => {
      if (event.altKey === true && event.ctrlKey === true && event.key === KeyCode.I) {
        this.open();
      }
    });
  }

  open(): void {
    if (this.openModal === undefined) {
      this.openModal = this.modalService.open(AboutDialogComponent, undefined, {
        aboutData: this.getAboutData(),
        actuatorData: this.getActuatorData()
      });
      this.openModal.afterClose().subscribe((): void => this.openModal = undefined);
    } else {
      this.openModal.close();
    }
  }

  /**
   * getActuatorData
   * Fetch actuator info to display on about dialog
   * Unlike getAboutData this does not come from configuration call
   *
   * @returns A promise of the response data containing actuator info
   */
  getActuatorData(): Observable<ActuatorInfo> {
    if (this.$actuatorData === undefined) {
      const url = this.linkRegistry.getLink('link_actuator');
      this.$actuatorData = this.httpClient.get<never>(url)
        .pipe(
          map((data: ActuatorDataServer): ActuatorInfo => ({
            buildTag: data['Build-Tag'],
            buildNumber: data['Build-Number'],
            gitRevision: data['Git-Revision']
          })),
          shareReplay(1)
        );
    }
    return this.$actuatorData;
  }

  /**
   * This function get user agent, platform, and app version from browser and
   * Webbroker revision and version information from configuration service
   *
   * @returns Returns user agent, platform, app version, revision, and
   * version.
   */
  private getAboutData(): AboutData {
    return {
      browser: {
        userAgent: this.browserWindow.navigator.userAgent,
        platform: this.browserWindow.navigator.platform,
        appVersion: this.browserWindow.navigator.appVersion
      },
      application: {
        revision: this.revision ?? 'N/A',
        version: this.version ?? 'N/A',
        locationId: this.locationId ?? 'N/A'
      }
    };
  }
}
